import React, { useState } from 'react';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  Paper,
  CircularProgress,
  Link
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useAnalytics } from '../context/AnalyticsContext';
import { ButtonEvents } from '../services/analytics/events';
import WaitlistStatusCard from '../components/WaitlistStatusCard';
import WaitlistSignupCard from '../components/WaitlistSignupCard';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  margin: theme.spacing(2, 0),
  borderRadius: theme.spacing(2),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
    margin: theme.spacing(1, 0),
  },
}));

const StyledForm = styled('form')(({ theme }) => ({
  width: '100%',
  marginTop: theme.spacing(2),
}));

/**
 * WaitlistStatusPage component for checking waitlist status
 * Handles email validation, status checking, and different user states
 * @component
 */
const WaitlistStatusPage = () => {
  const navigate = useNavigate();
  const { trackEvent } = useAnalytics();
  
  const [formData, setFormData] = useState({
    email: ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [waitlistData, setWaitlistData] = useState(null);
  const [showSignupOption, setShowSignupOption] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [statusType, setStatusType] = useState('info'); // 'success', 'error', 'info'

  const validateEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear field errors when user types
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    
    // Clear status messages when user types
    if (statusMessage) {
      setStatusMessage('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const email = formData.email.trim();
    
    // Validate email
    if (!email) {
      setErrors({ email: 'Email is required' });
      return;
    }
    
    if (!validateEmail(email)) {
      setErrors({ email: 'Please enter a valid email address' });
      return;
    }

    setIsLoading(true);
    setErrors({});
    setStatusMessage('');

    try {
      // Track analytics event
      trackEvent(ButtonEvents.WAITLIST_STATUS_CHECK);

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}user/waitlist/status/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (response.ok) {
        if (data.status === 'existing_user') {
          // User already has an account - redirect to login
          setStatusMessage(data.message);
          setStatusType('info');
          
          // Track existing user event
          trackEvent(ButtonEvents.WAITLIST_STATUS_EXISTING_USER);
          
          // Redirect to login after showing message
          setTimeout(() => {
            navigate('/login', { 
              state: { 
                message: 'Your account is already active! Please log in to continue.',
                email: email
              }
            });
          }, 3000);
          
        } else if (data.status === 'on_waitlist') {
          // User is on waitlist - show status
          setWaitlistData(data.waitlist_data);
          
          // Track waitlist status found event
          trackEvent(ButtonEvents.WAITLIST_STATUS_FOUND, {
            position: data.waitlist_data.priority,
            is_approved: data.is_approved
          });
          
        }
      } else if (response.status === 404) {
        // User not found on waitlist
        setShowSignupOption(true);
        setStatusMessage(data.message);
        setStatusType('info');
        
        // Track not found event
        trackEvent(ButtonEvents.WAITLIST_STATUS_NOT_FOUND);
        
      } else {
        throw new Error(data.error || 'Failed to check waitlist status');
      }

    } catch (error) {
      console.error('Waitlist status check error:', error);
      setStatusMessage(error.message || 'Unable to check status. Please try again.');
      setStatusType('error');
      
      // Track error event
      trackEvent(ButtonEvents.WAITLIST_STATUS_ERROR, {
        error: error.message
      });
    } finally {
      setIsLoading(false);
    }
  };

  // If we have waitlist data, show the status card
  if (waitlistData) {
    return (
      <Container component="main" maxWidth="md">
        <WaitlistStatusCard waitlistData={waitlistData} />
      </Container>
    );
  }

  // If showing signup option, show the signup card
  if (showSignupOption) {
    return (
      <Container component="main" maxWidth="md">
        <WaitlistSignupCard email={formData.email} />
      </Container>
    );
  }

  // Default form view
  return (
    <Container component="main" maxWidth="md">
      <Box
        sx={{
          marginTop: { xs: 4, md: 8 },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <StyledPaper elevation={3}>
          <Typography 
            component="h1" 
            variant="h4" 
            align="center" 
            gutterBottom
            color="primary"
          >
            Check Your Waitlist Status
          </Typography>
          
          <Typography 
            variant="body1" 
            align="center" 
            color="text.secondary"
            sx={{ mb: 3 }}
          >
            Enter your email address to see your current position on the waitlist
            and get your referral link to skip ahead.
          </Typography>

          {statusMessage && (
            <Alert 
              severity={statusType} 
              sx={{ mb: 2 }}
              onClose={() => setStatusMessage('')}
            >
              {statusMessage}
            </Alert>
          )}

          <StyledForm onSubmit={handleSubmit} noValidate>
            <TextField
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              type="email"
              autoComplete="email"
              autoFocus
              value={formData.email}
              onChange={handleInputChange}
              error={!!errors.email}
              helperText={errors.email}
              disabled={isLoading}
              sx={{ mb: 3 }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={isLoading}
              startIcon={isLoading && <CircularProgress size={20} />}
              sx={{ mb: 2 }}
            >
              {isLoading ? 'Checking Status...' : 'Check My Status'}
            </Button>
          </StyledForm>

          <Box sx={{ textAlign: 'center', mt: 3 }}>
            <Typography variant="body2" color="text.secondary">
              Don't have an account yet?{' '}
              <Link 
                component="button"
                variant="body2"
                onClick={() => navigate('/signup')}
                sx={{ textDecoration: 'none' }}
              >
                Join the waitlist
              </Link>
            </Typography>
          </Box>
        </StyledPaper>
      </Box>
    </Container>
  );
};

export default WaitlistStatusPage; 